/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InsuranceCitation.module.scss';

export const InsuranceCitation = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.15 });

  return (
    <Section
      background="radial-gradient(ellipse 50% 50% at 12% 0%, rgba(51, 92, 244, 0.08), transparent 70%), radial-gradient(ellipse 45% 45% at 90% 100%, rgba(1, 83, 244, 0.06), transparent 70%), #F7F9FD"
      className={styles.root}
    >
      <div className={styles.head}>
        <TitleBlock
          title={t('Everyone cites something. We cite the registry.')}
          description={t(
            'A rating decision, a declined claim or a sanctions call has to survive a file review years later. What matters is where the citation lands.',
          )}
        />
      </div>

      <div className={cn(styles.chain, revealed && styles.chainIn)} ref={ref}>
        <span className={styles.vs} aria-hidden="true">
          VS
        </span>

        <div className={styles.col}>
          <div className={styles.colInner}>
            <span className={styles.colLabel}>{t('Derived providers')}</span>

            <span className={styles.node}>{t('Registries and other inputs')}</span>
            <span className={styles.conn} />
            <span className={styles.node}>{t('Blended, standardised, scored')}</span>
            <span className={styles.conn} />
            <span className={cn(styles.node, styles.nodeLast)}>{t('Vendor record')}</span>

            <div className={styles.cit}>
              <div className={styles.citHead}>
                <span className={styles.dots}>
                  <i />
                  <i />
                  <i />
                </span>
                {t('What the citation gives you')}
              </div>
              <dl className={styles.cr}>
                <dt>{t('Source')}</dt>
                <dd className={styles.mut}>{t('Vendor company file')}</dd>
              </dl>
              <dl className={styles.cr}>
                <dt>{t('Reference')}</dt>
                <dd className={styles.mut}>{t('Internal record identifier')}</dd>
              </dl>
              <dl className={styles.cr}>
                <dt>{t('Last update')}</dt>
                <dd className={styles.mut}>{t('Vendor refresh cycle')}</dd>
              </dl>
              <dl className={styles.cr}>
                <dt>{t('Filing')}</dt>
                <dd className={styles.mut}>{t('Not carried through')}</dd>
              </dl>
              <dl className={styles.cr}>
                <dt>{t('Access')}</dt>
                <dd className={styles.mut}>{t('Vendor portal, licence required')}</dd>
              </dl>
            </div>

            <p className={styles.note}>
              {t("The citation resolves to the vendor's own dataset. To re-check a field, you go back to the vendor.")}
            </p>
          </div>
        </div>

        <div className={cn(styles.col, styles.colAccent)}>
          <div className={styles.colInner}>
            <span className={styles.colLabel}>Global Database</span>

            <span className={styles.node}>{t('Government registry filing')}</span>
            <span className={styles.conn} />
            <span className={cn(styles.node, styles.nodeLast, styles.nodeStrong)}>
              {t('Your record, with the filing reference attached')}
            </span>

            <div className={cn(styles.cit, styles.citAccent)}>
              <div className={styles.citHead}>
                <span className={styles.dots}>
                  <i />
                  <i />
                  <i />
                </span>
                {t('What the citation gives you')} <span className={styles.sample}>{t('SAMPLE')}</span>
              </div>
              <dl className={styles.cr}>
                <dt>{t('Registry')}</dt>
                <dd>
                  <span className={styles.fl}>🇬🇧 Companies House, {t('United Kingdom')}</span>
                </dd>
              </dl>
              <dl className={styles.cr}>
                <dt>{t('Filing')}</dt>
                <dd>
                  <span className={styles.lit}>PSC01</span> · {t('persons with significant control')}
                </dd>
              </dl>
              <dl className={styles.cr}>
                <dt>{t('Filed')}</dt>
                <dd>11 Mar 2026</dd>
              </dl>
              <dl className={styles.cr}>
                <dt>{t('Retrieved')}</dt>
                <dd>
                  <span className={styles.lit}>28 Jul 2026, 06:14:11 UTC</span>
                </dd>
              </dl>
              <dl className={styles.cr}>
                <dt>{t('Source')}</dt>
                <dd>
                  <span className={styles.lit}>find-and-update.company-information.service.gov.uk</span>
                  <span className={cn(styles.lit, styles.path)}>
                    /company/08841207/persons-with-significant-control
                  </span>
                </dd>
              </dl>
            </div>

            <p className={styles.note}>
              {t(
                'The citation resolves to the statutory record. Your reviewer, your auditor or a regulator goes straight to the source and confirms it.',
              )}
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
};
